import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
	Authors,
	DOCUMENT_TREE_MODE,
	DocumentTreeComponent,
	EditDocumentMeta,
	MoveDocumentsToFolderEvent,
} from '@components/document-tree/document-tree.component';
import { getDocumentNonHashedId } from '@core/util/documents';
import { ICON } from '@core/util/icon';
import { getFullName } from '@core/util/string';
import { BusinessUser } from '@models/businesses';
import { BusinessDocumentV2 } from '@models/documents';
import {
	DefaultFolder,
	DefaultTemplateDocumentV2,
	Document,
	Folder,
	MatterFileType,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessDocumentTemplatesService } from '@repos/document-templates/business-document-templates.service';
import { BusinessFoldersService } from '@repos/folders/business-folders.service';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import {
	BusinessDocumentsDispatch,
	BusinessDocumentsState,
} from '@state/business/documents';
import { BusinessFoldersDispatch } from '@state/business/folders/folders.dispatch';
import { BusinessFoldersState } from '@state/business/folders/folders.state';
import { combineLatest, from, Observable, of } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	take,
	withLatestFrom,
} from 'rxjs/operators';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import {
	TemplateFolderDeleteDialogClose,
	TemplateFolderDeleteDialogComponent,
	TemplateFolderDeleteDialogData,
} from './template-folder-delete-dialog/template-folder-delete-dialog.component';
import {
	TemplateFolderEditDialogClose,
	TemplateFolderEditDialogComponent,
	TemplateFolderEditDialogData,
} from './template-folder-edit-dialog/template-folder-edit-dialog.component';
import {
	TemplateMoveDialogClose,
	TemplateMoveDialogComponent,
	TemplateMoveDialogData,
} from './template-move-dialog/template-move-dialog.component';
import { UserProfileState } from '@state/user-profile';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export enum FilesAndFoldersDropdownActions {
	ADD_DOCUMENT = 'add-document',
	ADD_BLANK_DOCUMENT = 'add-blank-document',
	ADD_FOLDER = 'add-folder',
}

const addItemDropdownOptions: DropdownOption<FilesAndFoldersDropdownActions>[] =
	[
		{
			name: 'Add new folder',
			value: FilesAndFoldersDropdownActions.ADD_FOLDER,
			icon: ICON.FOLDER,
		},
		{
			name: 'Add new template',
			value: FilesAndFoldersDropdownActions.ADD_DOCUMENT,
			icon: ICON.DOCUMENT,
		},
	];

const fileTypeTabs: ButtonGroupOption<MatterFileType>[] = [
	{ label: 'Sale', value: MatterFileType.SALE },
	{ label: 'Purchase', value: MatterFileType.PURCHASE },
	{ label: 'Refinance', value: MatterFileType.REFINANCE },
	{ label: 'Unassigned', value: null },
];

@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-documents-templates',
	templateUrl: 'templates.component.html',
	standalone: false,
})
export class DocumentsTemplatesComponent implements OnInit {
	AuthorIds$: Observable<Authors>;
	@Select(BusinessUsersState.getUsers) businessUsers$!: Observable<
		BusinessUser[]
	>;

	currentTab: MatterFileType = MatterFileType.SALE;
	@Select(BusinessDocumentsState.getAll) documents$: Observable<
		BusinessDocumentV2[]
	>;
	@SelectSnapshot(BusinessDocumentsState.getBySource) getDocumentBySourceFn: (
		sourceId: string,
	) => BusinessDocumentV2;
	@ViewChild('documentTree') documentTree!: DocumentTreeComponent;
	dropdownOptions = addItemDropdownOptions;
	fileTypeTabs = [...fileTypeTabs];
	@Select(BusinessFoldersState.getAll) folders$: Observable<Folder[]>;
	ICON = ICON;
	@Select(AuthState.getLoggedInUserId) loggedInUserId$!: Observable<string>;
	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId!: string;
	@SelectSnapshot(AuthState.getLoggedInUserId) loggedInUserId!: string;
	searchText = '';
	showDropdownOptions = false;
	documentTreeMode = DOCUMENT_TREE_MODE.MANAGE;

	get rootLabel(): string {
		return 'Root';
	}

	constructor(
		private activatedRoute: ActivatedRoute,
		private businessDocumentsDispatch: BusinessDocumentsDispatch,
		private businessDocumentTemplatesService: BusinessDocumentTemplatesService,
		private businessFoldersService: BusinessFoldersService,
		private dialog: MatDialog,
		private folderDispatch: BusinessFoldersDispatch,
		private navigationService: NavigationService,
		private snackbarService: SnackbarService,
		private drawerService: DrawerService,
	) {
		this.AuthorIds$ = combineLatest([this.documents$, this.folders$]).pipe(
			untilDestroyed(this),
			// create an array of unique ids that we'll need
			map(([documents, folders]) =>
				[
					...new Set([
						...documents.map((document) => document.lastEditedBy),
						...folders.map((folder) => folder.author),
					]),
				]
					.filter((authorId) => !!authorId)
					.sort(),
			),
			// ensure uniqueness
			distinctUntilChanged(
				(prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
			),
			// flip it back to a set for speedier processing
			map((authorIds) => new Set(authorIds)),
			// now that we have our ids, lets switch it over
			// listen for business user updates
			switchMap((authorIds) =>
				this.businessUsers$.pipe(
					// filter the users to only those that are in our set
					map((users) => users.filter((user) => authorIds.has(user.id))),
					// and finally, coerce into our expected format
					map((users) => {
						const authors: Authors = {
							'': 'Closer',
						};
						users.forEach(({ id, firstName, lastName }) => {
							authors[id] = getFullName([firstName, lastName]);
						});

						return authors;
					}),
				),
			),
		);
	}

	onDropdownAction(
		event: DropdownResult<DropdownOption<FilesAndFoldersDropdownActions>>,
	): void {
		switch (event.result.value) {
			case FilesAndFoldersDropdownActions.ADD_DOCUMENT:
				this.addDocument();
				break;

			case FilesAndFoldersDropdownActions.ADD_FOLDER:
				this.addFolder();
		}
	}

	toggleDropdown(): void {
		this.showDropdownOptions = !this.showDropdownOptions;
	}

	hideDropdown(): void {
		this.showDropdownOptions = false;
	}

	ngOnInit(): void {
		this.activatedRoute.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((params) =>
				this.changeSelectedTab(
					params.type ? (params.type as MatterFileType) : MatterFileType.SALE,
				),
			);
	}

	addDocument(folderId: string = ''): void {
		this.getFolderById$(folderId)
			.pipe(
				untilDestroyed(this),
				take(1),
				withLatestFrom(this.loggedInUserId$),
				map(([folder, author]) => {
					const document: BusinessDocumentV2 = {
						...DefaultTemplateDocumentV2,
						lastEditedBy: author,
						alwaysExists: !!folderId,
						createdAt: new Date().getTime(),
						folderId,
						matterType: folder?.matterType || [this.currentTab],
						title: 'Untitled Template',
					};

					return document;
				}),
				switchMap((document) =>
					from(
						this.businessDocumentTemplatesService.createBlankDocument(document),
					).pipe(
						take(1),
						map(
							(businessDocumentV2: BusinessDocumentV2) =>
								({
									folderId,
									documentId: businessDocumentV2.source,
								}) as EditDocumentMeta,
						),
					),
				),
			)
			.subscribe((meta) => {
				this.editDocument(meta);
			});
	}

	addFolder(folderId: string = ''): void {
		const folderObs$ = folderId
			? this.getFolderById$(folderId)
			: of<Folder>({
					...DefaultFolder,
					matterType: [this.currentTab],
				});

		folderObs$
			.pipe(
				untilDestroyed(this),
				take(1),
				withLatestFrom(this.loggedInUserId$),
				switchMap(([folder, author]) => {
					const FolderData: TemplateFolderEditDialogData = {
						author,
						currentFolder: DefaultFolder,
						folder: null,
						matterFileTypes: folder.matterType,
						parentId: folderId,
						showMatterTypes: folder.matterType,
					};

					return this.dialog
						.open<
							TemplateFolderEditDialogComponent,
							TemplateFolderEditDialogData,
							TemplateFolderEditDialogClose
						>(TemplateFolderEditDialogComponent, {
							data: FolderData,
							maxWidth: '350px',
							width: '95vw',
						})
						.afterClosed()
						.pipe(take(1));
				}),
				filter((folder) => !!folder),
			)
			.subscribe((folder) => {
				void this.businessFoldersService.Create(folder);
			});
	}

	// folders
	canCreateFolder(): boolean {
		return true;
	}

	// disables ALL movements on documents that require context
	canMoveDocument(document: Document): boolean {
		return !document.requiresContext;
	}

	// disable ALL movements on documents that require context
	canMoveDocumentToFolder(document: Document): boolean {
		return !document.requiresContext;
	}

	changeSelectedTab(tab: MatterFileType): void {
		const { documentTree } = this;
		if (documentTree) {
			documentTree.clearSelected();
		}
		this.currentTab = tab;
	}

	copyDocument(documentId: string): void {
		this.getDocumentById$(documentId)
			.pipe(
				untilDestroyed(this),
				take(1),
				withLatestFrom(this.loggedInUserId$),
				switchMap(([document]) => {
					return from(
						this.businessDocumentTemplatesService.copyDocument(
							getDocumentNonHashedId(document),
						),
					).pipe(
						take(1),
						map(
							() =>
								({
									documentId: document.id,
									folderId: document.folderId,
								}) as EditDocumentMeta,
						),
					);
				}),
				filter((newDocument) => !!newDocument),
			)
			.subscribe((newDocument) => {
				this.documentTree.selectedDocumentIds = new Set([
					newDocument.documentId,
				]);
			});
	}

	deleteDocument(sourceId: string): void {
		const doc = this.getDocumentBySourceFn(sourceId);
		void this.businessDocumentTemplatesService.deleteDocument(
			getDocumentNonHashedId(doc),
		);
	}

	deleteFolder(folderId: string): void {
		this.getFolderById$(folderId)
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((folder) => !!folder),
				switchMap((folder) =>
					this.dialog
						.open<
							TemplateFolderDeleteDialogComponent,
							TemplateFolderDeleteDialogData,
							TemplateFolderDeleteDialogClose
						>(TemplateFolderDeleteDialogComponent, {
							data: { folder },
							maxWidth: '350px',
							width: '95vw',
						})
						.afterClosed()
						.pipe(
							take(1),
							filter((deletedFolder) => !!deletedFolder),
						),
				),
			)
			.subscribe(({ id }) => {
				this.folderDispatch.Remove(id);
			});
	}

	editDocument(documentMeta: EditDocumentMeta): void {
		this.navigationService.goToBusinessManageTemplate(
			documentMeta.documentId,
			this.currentTab,
		);
	}

	editFolder(folderId: string): void {
		this.getFolderById$(folderId)
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((folder) => !!folder),
				switchMap((folder) => {
					const FolderData: TemplateFolderEditDialogData = {
						folder,
						author: folder.author,
						matterFileTypes: folder.matterType,
						parentId: folder.parentId,
						showMatterTypes: folder.matterType,
					};

					return this.dialog
						.open<
							TemplateFolderEditDialogComponent,
							TemplateFolderEditDialogData,
							TemplateFolderEditDialogClose
						>(TemplateFolderEditDialogComponent, {
							data: FolderData,
							maxWidth: '350px',
							width: '95vw',
						})
						.afterClosed()
						.pipe(
							take(1),
							filter((updatedFolder) => !!updatedFolder),
						);
				}),
			)
			.subscribe((folder) => {
				void this.businessFoldersService.Update(folder.id, folder);
			});
	}

	moveDocument(documentId: string): void {
		this.getDocumentById$(documentId)
			.pipe(
				untilDestroyed(this),
				take(1),
				switchMap((document) =>
					this.dialog
						.open<
							TemplateMoveDialogComponent,
							TemplateMoveDialogData,
							TemplateMoveDialogClose
						>(TemplateMoveDialogComponent, {
							data: {
								document,
							},
							maxWidth: '600px',
							minWidth: '300px',
							width: '50%',
						})
						.afterClosed()
						.pipe(
							take(1),
							filter((folderId) => !!folderId || folderId === ''),
							switchMap((folderId) => this.getFolderById$(folderId)),
							map((folder) => ({ folder, document })),
						),
				),
			)
			.subscribe(({ folder, document }) => {
				this.deselectAllDocuments();
				this.businessDocumentsDispatch.MoveDocumentsToFolder(
					[document],
					folder.id ?? '',
				);
				this.snackbarService.openSnackBar({
					text: `<span class='font-bold'>${document.title}</span> has been moved to <span class='font-bold'>${
						folder?.title || this.rootLabel
					}</span>`,
					type: 'info',
				});
			});
	}

	moveDocumentsToFolder({
		documents,
		folderId,
	}: MoveDocumentsToFolderEvent): void {
		this.businessDocumentsDispatch.MoveDocumentsToFolder(
			documents as BusinessDocumentV2[],
			folderId,
		);
		this.getFolderById$(folderId)
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((folder) => !!folder || folderId === ''),
			)
			.subscribe((folder) => {
				let text = `<span class='font-bold'>${documents[0]?.title}</span> has been moved to <span class='font-bold'>${
					folder?.title || this.rootLabel
				}</span>`;
				if (documents.length > 1) {
					text = `<span class='font-bold'>${documents.length} documents</span> moved to <span class='font-bold'>${
						folder?.title || this.rootLabel
					}</span>`;
				}
				this.deselectAllDocuments();
				this.snackbarService.openSnackBar({
					text,
					type: 'info',
				});
			});
	}

	onEndItemClick(): void {
		this.searchText = '';
	}

	onSelectedDocuments(evt: string[]): void {
		this.businessDocumentsDispatch.SelectDocumentIds(evt);
	}

	deselectAllDocuments(): void {
		this.selectDocumentIds([]);
	}

	private getDocumentById$(documentId: string): Observable<BusinessDocumentV2> {
		return this.documents$.pipe(
			map((documents) => documents.find(({ id }) => id === documentId)),
		);
	}

	private getFolderById$(folderId: string): Observable<Folder> {
		return this.folders$.pipe(
			map((folders) => folders.find(({ id }) => id === folderId)),
		);
	}

	private selectDocumentIds(documentIds: string[]): void {
		this.documentTree.selectedDocumentIds = new Set(documentIds);
	}
}
